import { detectCaptureClient, type CaptureClient } from '@/lib/capture-heartbeat'
import { trackClientEvent, type ClientEventFields } from '@/lib/telemetry'

/**
 * Why the microphone died, for telemetry (CLA-1606).
 *
 * The capture heartbeat tells the server THAT a web recording's track is dead
 * (`track_live: false`) and nothing about why. An iPhone browser suspending
 * capture on screen-lock, a Bluetooth headset dropping out, and the clinician
 * revoking the site's microphone permission all look the same from there, and
 * they need different advice. iOS already reports `mic_lost` /
 * `mic_recovered` with a cause; this is the web half, under the same names
 * (both allowlisted server-side) with `platform: 'web'`.
 *
 * Telemetry only. Nothing about the recording — the notice, the frozen timer,
 * the heartbeat — waits on or reads anything here.
 *
 * ⚠ Device ids, group ids and labels are read to decide `device_removed` and
 * are NEVER sent. A label is whatever the OS calls the device ("Sarah's
 * AirPods"); `device_class` is the coarse browser class and nothing finer.
 */

export type MicLossCause =
  /** The track went `ended` with its device still present: the OS or browser took it. */
  | 'track_ended'
  /** The track is `muted` — what WebKit does on screen-lock or app switch. */
  | 'track_muted'
  /** The device the track was capturing from is no longer enumerated (headset unplugged / disconnected). */
  | 'device_removed'
  /** The site's microphone permission now reads `denied`. */
  | 'permission_revoked'
  /** The MediaRecorder failed while the track still looked live. */
  | 'recorder_error'
  | 'unknown'

/**
 * What noticed the loss. `tab_return` means the track died while the page was
 * hidden and its events never reached us, so `visibility` (read at detection)
 * says `visible` even though the loss happened while hidden.
 */
export type MicLossDetectedBy = 'track_event' | 'recorder_error' | 'tab_return' | 'resume'

export interface MicLossEvidence {
  readyState?: string
  muted?: boolean
  recorderFailed?: boolean
  /** The microphone permission state, or null when this browser cannot say. */
  permission?: PermissionState | null
  /** Whether the track's device is still enumerated, or null when we cannot tell. */
  devicePresent?: boolean | null
}

/**
 * The most specific cause the evidence supports. Ordered by how much each one
 * explains: a revoked permission or a removed device ENDS the track, so an
 * ended track is only `track_ended` when neither of those is true; a recorder
 * error under a dead track is a consequence of the track, not a cause.
 */
export function classifyMicLoss(e: MicLossEvidence): MicLossCause {
  if (e.permission === 'denied') return 'permission_revoked'
  if (e.devicePresent === false) return 'device_removed'
  if (e.readyState === 'ended') return 'track_ended'
  if (e.muted === true) return 'track_muted'
  if (e.recorderFailed) return 'recorder_error'
  return 'unknown'
}

/** The parts of `navigator` the evidence is read from — replaceable for tests. */
export interface MicEnvironment {
  queryPermission?: () => Promise<PermissionState>
  enumerateDevices?: () => Promise<Array<Pick<MediaDeviceInfo, 'kind' | 'deviceId' | 'groupId'>>>
}

function browserEnvironment(): MicEnvironment {
  const nav = typeof navigator === 'undefined' ? undefined : navigator
  return {
    queryPermission: nav?.permissions?.query
      ? async () => (await nav.permissions.query({ name: 'microphone' as PermissionName })).state
      : undefined,
    enumerateDevices: nav?.mediaDevices?.enumerateDevices
      ? () => nav.mediaDevices.enumerateDevices()
      : undefined,
  }
}

/**
 * Whether the track's device is still in the list. Matches the group id too
 * where there is one: Chrome's `default` pseudo-device keeps its device id
 * when the headset behind it goes away and the default falls back to the
 * built-in mic — only the group id changes.
 */
export function devicePresent(
  settings: { deviceId?: string; groupId?: string },
  devices: Array<Pick<MediaDeviceInfo, 'kind' | 'deviceId' | 'groupId'>>,
): boolean | null {
  if (!settings.deviceId) return null
  return devices.some(
    (d) =>
      d.kind === 'audioinput' &&
      d.deviceId === settings.deviceId &&
      (!settings.groupId || !d.groupId || d.groupId === settings.groupId),
  )
}

/**
 * Read what the browser will tell us about a loss. Each probe is optional and
 * contained: a browser that cannot answer contributes null, never an error.
 */
export async function gatherMicEvidence(
  stream: MediaStream,
  recorderFailed: boolean,
  env: MicEnvironment = browserEnvironment(),
): Promise<MicLossEvidence> {
  const track = stream.getAudioTracks?.()?.[0]
  const evidence: MicLossEvidence = {
    readyState: track?.readyState,
    muted: track?.muted,
    recorderFailed,
    permission: null,
    devicePresent: null,
  }
  const [permission, devices] = await Promise.all([
    env.queryPermission?.().catch(() => null) ?? null,
    env.enumerateDevices?.().catch(() => null) ?? null,
  ])
  evidence.permission = permission
  if (devices && track?.getSettings) {
    try {
      evidence.devicePresent = devicePresent(track.getSettings(), devices)
    } catch {
      /* no settings on this track — leave it unknown */
    }
  }
  return evidence
}

/**
 * Beyond this many losses in one capture the track is flapping, and another
 * pair of events adds rows, not information.
 */
export const MAX_EPISODES_PER_CAPTURE = 20

type Emit = (
  event: 'mic_lost' | 'mic_recovered',
  metadata: Record<string, string | number | boolean>,
  fields: ClientEventFields,
) => void

interface MicLossReporterOptions {
  emit?: Emit
  now?: () => number
  visibility?: () => string
  client?: CaptureClient
  env?: MicEnvironment
}

/**
 * One capture's loss episodes. An episode opens at the first `lost()` and
 * closes at `recovered()` or `stop()`; inside it every further `lost()` is
 * ignored, so a muted track that then ends, or a tab return that re-checks a
 * track already known dead, never sends a second `mic_lost`. A `mic_recovered`
 * is only ever sent for an episode that sent `mic_lost`, and carries the same
 * cause, so the pair can be joined. An episode that ends without recovering
 * leaves its `mic_lost` standing alone — which is what a death looks like.
 */
export class MicLossReporter {
  private open = false
  private episodes = 0
  private startedAt = 0
  private reported: Promise<MicLossCause | null> = Promise.resolve(null)
  /**
   * Every emission queues behind the previous one: the probes are async, so
   * without this a quick second loss could be sent before the first one's
   * recovery and the series would read out of order.
   */
  private chain: Promise<unknown> = Promise.resolve()
  private stopped = false
  private readonly emit: Emit
  private readonly now: () => number
  private readonly visibility: () => string
  private readonly client: CaptureClient
  private readonly env: MicEnvironment | undefined
  private readonly noteId: number
  private readonly stream: MediaStream

  constructor(noteId: number, stream: MediaStream, opts: MicLossReporterOptions = {}) {
    this.noteId = noteId
    this.stream = stream
    this.emit = opts.emit ?? ((event, metadata, fields) => trackClientEvent(event, metadata, fields))
    this.now = opts.now ?? Date.now
    this.visibility =
      opts.visibility ?? (() => (typeof document === 'undefined' ? 'unknown' : document.visibilityState))
    this.client = opts.client ?? detectCaptureClient()
    this.env = opts.env
  }

  /**
   * The mic is gone. Opens an episode (no-op inside one) and reports it once
   * the evidence is in. Visibility is read NOW, synchronously: by the time the
   * probes answer the clinician may have come back to the tab.
   */
  lost(detectedBy: MicLossDetectedBy, opts: { recorderFailed?: boolean } = {}): void {
    if (this.stopped || this.open || this.episodes >= MAX_EPISODES_PER_CAPTURE) return
    this.open = true
    this.episodes++
    this.startedAt = this.now()
    const visibility = this.visibility()
    // Probe now, while the evidence is fresh; only the sending waits its turn.
    const classified = gatherMicEvidence(this.stream, opts.recorderFailed ?? false, this.env)
      .then(classifyMicLoss)
      .catch((): MicLossCause => 'unknown')
    this.reported = this.chain
      .then(() => classified)
      .then((cause) => {
        this.send(
          'mic_lost',
          {
            platform: 'web',
            cause,
            visibility,
            device_class: this.client,
            detected_by: detectedBy,
          },
          { note_id: this.noteId },
        )
        return cause
      })
    this.chain = this.reported
  }

  /** The mic is delivering again. Reports the gap, only for an episode that reported a loss. */
  recovered(): void {
    if (!this.open) return
    this.open = false
    const durationMs = Math.max(0, Math.round(this.now() - this.startedAt))
    const visibility = this.visibility()
    this.chain = this.reported.then((cause) => {
      if (cause === null) return
      this.send(
        'mic_recovered',
        { platform: 'web', cause, visibility, device_class: this.client },
        // `duration_ms` is a row column — the one iOS's `mic_recovered` fills.
        { note_id: this.noteId, duration_ms: durationMs },
      )
    })
  }

  /** A throwing sink must not break the chain every later event waits on. */
  private send(...args: Parameters<Emit>) {
    try {
      this.emit(...args)
    } catch {
      /* telemetry only */
    }
  }

  /** The capture is over. An open episode ends unrecovered; nothing further is sent. */
  stop(): void {
    this.stopped = true
    this.open = false
  }
}
